import { FC, memo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface MerchantGroupSectionProps extends SectionBaseProps {
    /** Currently selected merchant group value */
    selectedMerchantGroup: string;
    /** Available merchant group options */
    merchantGroupOptions: SelectProps.Option[];
    /** Callback when merchant group selection changes */
    handleMerchantGroupChange: (value: string) => void;
}

/**
 * Merchant group section — renders the merchant group dropdown.
 * Only shown for NCEMI and DEAL campaign types (parent controls visibility).
 * Owns its own i18n bundle (`organisms.merchantGroupSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const MerchantGroupSection: FC<MerchantGroupSectionProps> = ({
    selectedMerchantGroup,
    merchantGroupOptions,
    handleMerchantGroupChange,
    mode,
    disabled,
}) => {
    const { translate } = useTranslate('organisms.merchantGroupSection');
    // Merchant group is read-only after creation — disabled in both view and modify modes.
    // Only editable during create.
    const isDisabled = disabled ?? mode !== 'create';

    return (
        <Container header={<Header variant="h2">{translate('section_merchant_group')}</Header>}>
            <FormField label={translate('merchant_group_label')}>
                <Select
                    selectedOption={
                        merchantGroupOptions.find((o) => o.value === selectedMerchantGroup) ?? null
                    }
                    onChange={({ detail }) =>
                        handleMerchantGroupChange(detail.selectedOption.value ?? '')
                    }
                    options={merchantGroupOptions}
                    placeholder={translate('merchant_group_placeholder')}
                    ariaRequired={true}
                    disabled={isDisabled}
                    data-testid="merchant-group"
                />
            </FormField>
        </Container>
    );
};

export default memo(MerchantGroupSection);
